import {
    Add,
    Book1,
    Chart,
    ClipboardTick,
    DocumentText,
    Grid1,
    Image as ImageIcon,
    Location,
    SeparatorHorizontal,
} from '@/components/icons';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuSeparator,
    ContextMenuShortcut,
    ContextMenuSub,
    ContextMenuSubContent,
    ContextMenuSubTrigger,
    ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { EDITOR_OVERLAY_Z_CLASSNAME } from '@/components/ui/RichTextEditor/constants';
import { APP_FONT } from '@/constants';
import { primaryModifierCombo } from '@/lib/platform';
import type { PaidEntitlementPlan } from '@/api/entitlements';
import type { IconComponent } from '@/components/icons';
import type { ReactNode } from 'react';

/** Toolbox ids understood by `generateNewBlock` — plus `tool_test`, which the
 *  caller routes to the instrument selector instead of creating a block. */
const INSERT_ITEMS: Array<{ toolId: string; label: string; icon: IconComponent }> = [
    { toolId: 'tool_test', label: 'Instrumento', icon: Book1 },
    { toolId: 'tool_table', label: 'Tabela', icon: Grid1 },
    { toolId: 'tool_text', label: 'Texto', icon: DocumentText },
    { toolId: 'tool_chart', label: 'Gráfico', icon: Chart },
    { toolId: 'tool_divider', label: 'Divisor', icon: SeparatorHorizontal },
];

const IMAGE_INSERT_ITEM = {
    toolId: 'tool_image',
    label: 'Imagem',
    icon: ImageIcon,
} satisfies { toolId: string; label: string; icon: IconComponent };

interface PageContextMenuProps {
    children: ReactNode;
    /** Report is concluded: the menu degrades to "Ir para" only. */
    disabled: boolean;
    /** Blocks were copied inside the app, which only changes the item's wording —
     *  the paste item is always offered, because it also handles the system
     *  clipboard (a spreadsheet, a screenshot, text from anywhere) and knowing
     *  whether *that* holds anything would cost a permission prompt per open. */
    hasBlockClipboard: boolean;
    isCustomSectionBlocked: boolean;
    customSectionPlan: { id: PaidEntitlementPlan; name: string } | null;
    sections: Array<{ id: string; title: string }>;
    /** Image blocks are behind a feature flag; the caller decides. */
    canInsertImage?: boolean;
    onInsert: (toolId: string) => void;
    onPaste: () => void;
    onAddCustomSection: () => void;
    onGoToSection: (sectionId: string) => void;
}

/**
 * Right-click menu for the report page — the surface with no chrome of its own.
 *
 * Purely presentational, like `SectionContextMenu` and `TableContextMenu`.
 * Mounted on `.report-paper`, so it is the fallback for anything on the page
 * that does not carry an inner menu; section text and table cells stop
 * propagation on their own triggers and never reach it.
 */
export function PageContextMenu({
    children,
    disabled,
    hasBlockClipboard,
    isCustomSectionBlocked,
    customSectionPlan,
    sections,
    canInsertImage = false,
    onInsert,
    onPaste,
    onAddCustomSection,
    onGoToSection,
}: PageContextMenuProps) {
    const insertItems = canInsertImage ? [...INSERT_ITEMS, IMAGE_INSERT_ITEM] : INSERT_ITEMS;

    return (
        <ContextMenu>
            <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
            {/* Above the floating section editor (z-[70]); portaled out of
                `.report-paper`, which re-pins light-theme tokens. */}
            <ContextMenuContent
                className={EDITOR_OVERLAY_Z_CLASSNAME}
                style={{ fontFamily: APP_FONT }}
            >
                {!disabled && (
                    <>
                        <ContextMenuSub>
                            <ContextMenuSubTrigger>
                                <div className="flex items-center gap-2">
                                    <Add className="size-4 text-slate-500" />
                                    Inserir
                                </div>
                            </ContextMenuSubTrigger>
                            <ContextMenuSubContent style={{ fontFamily: APP_FONT }}>
                                {insertItems.map((item) => (
                                    <ContextMenuItem
                                        key={item.toolId}
                                        onSelect={() => onInsert(item.toolId)}
                                    >
                                        <div className="flex items-center gap-2">
                                            <item.icon className="size-4 text-slate-500" />
                                            {item.label}
                                        </div>
                                    </ContextMenuItem>
                                ))}
                            </ContextMenuSubContent>
                        </ContextMenuSub>

                        <ContextMenuItem onSelect={onPaste}>
                            <div className="flex items-center gap-2">
                                <ClipboardTick className="size-4 text-slate-500" />
                                {hasBlockClipboard ? 'Colar blocos' : 'Colar'}
                            </div>
                            <ContextMenuShortcut>{primaryModifierCombo('V')}</ContextMenuShortcut>
                        </ContextMenuItem>

                        <ContextMenuSeparator />

                        <ContextMenuItem onSelect={onAddCustomSection}>
                            <div className="flex items-center gap-2">
                                <Add className="size-4 text-slate-500" />
                                Adicionar seção personalizada
                            </div>
                            {isCustomSectionBlocked && customSectionPlan ? (
                                <FeatureUnavailableChip
                                    availablePlan={customSectionPlan}
                                    side="left"
                                    className="ml-auto pl-2"
                                />
                            ) : null}
                        </ContextMenuItem>
                    </>
                )}

                {sections.length > 0 && (
                    <ContextMenuSub>
                        <ContextMenuSubTrigger>
                            <div className="flex items-center gap-2">
                                <Location className="size-4 text-slate-500" />
                                Ir para
                            </div>
                        </ContextMenuSubTrigger>
                        <ContextMenuSubContent
                            className="max-h-80 overflow-y-auto"
                            style={{ fontFamily: APP_FONT }}
                        >
                            {sections.map((section) => (
                                <ContextMenuItem
                                    key={section.id}
                                    onSelect={() => onGoToSection(section.id)}
                                >
                                    {section.title}
                                </ContextMenuItem>
                            ))}
                        </ContextMenuSubContent>
                    </ContextMenuSub>
                )}
            </ContextMenuContent>
        </ContextMenu>
    );
}
